import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router"
import useTitle from "@/hooks/useTitle"
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic"
import PulseLoader from "react-spinners/PulseLoader"
import Modal from "@/components/common/modal/Modal"
import {
  useGetAccountsQuery,
  useDeleteAccountMutation,
} from "./accountsApiSlice"

const EtDeleteAccount = ({ id, setActiveAdminView, userOrgId = "" }) => {
  // First Created - 27 Apr 2026
  // Author - Atul Pandey
  // Purpose - To delete / deactivate an Account

  useTitle("Delete Account")

  const { account } = useGetAccountsQuery(
    { ownerOrgId: userOrgId },
    {
      selectFromResult: ({ data }) => ({
        account: data?.entities[id],
      }),
    }
  )

  if (!account) {
    return <PulseLoader color={"#ea580c"} />
  }

  return (
    <EtDeleteAccountForm
      account={account}
      id={id}
      setActiveAdminView={setActiveAdminView}
    />
  )
}

const EtDeleteAccountForm = ({ account, id, setActiveAdminView }) => {
  const navigate = useNavigate()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [deleteMode, setDeleteMode] = useState("soft")

  const [
    deleteAccount,
    {
      isLoading,
      isSuccess,
      isError,
      error,
    },
  ] = useDeleteAccountMutation()

  const [formData, setFormData] = useState({
    accountName: account.accountName || "",
    accountDesc: account.accountDesc || "",
    accountStatus: account.accountStatus || "",
    accountStage: account.accountStage || "",
    contactOrganizationName:
      account?.contactOrganizationId?.contactOrganizationName || "",
  })

  useEffect(() => {
    if (isSuccess) {
      setFormData({
        accountName: "",
        accountDesc: "",
        accountStatus: "",
        accountStage: "",
        contactOrganizationName: "",
      })

      if (setActiveAdminView) {
        setActiveAdminView("accounts")
      } else {
        navigate("/accounts")
      }
    }
  }, [isSuccess])

  const handleOpenModal = (mode = "soft") => {
    setDeleteMode(mode)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => setIsModalOpen(false)

  const handleConfirm = async (e) => {
    e.preventDefault()

    try {
      await deleteAccount({ id, deleteMode }).unwrap()

      if (setActiveAdminView) {
        setActiveAdminView("accounts")
      } else {
        navigate("/accounts")
      }
    } catch (error) {
      console.error(error)
    } finally {
      handleCloseModal()
    }
  }

  const handleCancel = () => {
    if (setActiveAdminView) {
      setActiveAdminView("accounts")
    } else {
      navigate("/accounts")
    }
  }

  return (
    <div className="bg-gray-200 p-1 w-3/4 mx-auto mt-5 rounded-lg shadow-md shadow-black">
      {isLoading && <PulseLoader color={"#ea580c"} />}

      {isError && (
        <p className="text-orange-700 font-semi-bold py-2">
          {error?.data?.message}
        </p>
      )}

      <div className="bg-white p-1 m-1 rounded">
        <p className="p-1 m-1 font-bold font-poppins text-xl">
          Delete Account
        </p>

        <div className="bg-gray-200 rounded m-1 p-1">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              handleOpenModal("soft")
            }}
            className="m-1 p-1 text-xs font-poppins"
          >
            <input
              type="text"
              name="accountName"
              placeholder="Account name"
              value={formData.accountName}
              disabled
              className="w-full rounded p-2 m-1 bg-gray-100"
            />

            <input
              type="text"
              name="contactOrganizationName"
              placeholder="Client organization"
              value={formData.contactOrganizationName}
              disabled
              className="w-full rounded p-2 m-1 bg-gray-100"
            />

            <input
              type="text"
              name="accountDesc"
              placeholder="Account description"
              value={formData.accountDesc}
              disabled
              className="w-full rounded p-2 m-1 bg-gray-100"
            />

            <div className="grid grid-cols-2 gap-1">
              <input
                type="text"
                name="accountStatus"
                placeholder="Account status"
                value={formData.accountStatus}
                disabled
                className="w-full rounded p-2 m-1 bg-gray-100"
              />

              <input
                type="text"
                name="accountStage"
                placeholder="Account stage"
                value={formData.accountStage}
                disabled
                className="w-full rounded p-2 m-1 bg-gray-100"
              />
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded m-1 p-2">
              <p className="text-xs text-orange-700">
                You can either deactivate this Account or permanently delete it.
                Deactivation keeps the Account in history. Hard delete removes it
                permanently.
              </p>
            </div>

            <div id="accounts" className="bg-white m-1 px-1 py-3 rounded">
              <EtBtnFormBasic
                name={"Make Inactive"}
                type={"clear"}
                handleClick={() => handleOpenModal("soft")}
              />

              <EtBtnFormBasic
                name={"Hard Delete"}
                type={"create"}
                handleClick={() => handleOpenModal("hard")}
              />

              <EtBtnFormBasic
                name={"Cancel"}
                type={"cancel"}
                handleClick={handleCancel}
              />
            </div>
          </form>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirm}
      />
    </div>
  )
}

export default EtDeleteAccount